import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { FormatAmount } from '../components/format-amount/format-amount';
import { LotTimer } from '../components/lot/lot-timer';
import { LeaderboardTable } from '../components/table/leaderboard-table';
import type { LiveAuctionFeedSubscription } from '../graphql/types';
import {
  LiveAuctionFeedDocument,
  useLeaderboardsQuery,
  useMeQuery,
} from '../graphql/types';
import { color, gutter } from '../styles/mixins';
import { caps14, m18, m24, p14 } from '../styles/typography';
import { printRank } from '../utils/print-rank';

const StyleLotTimer = styled(LotTimer)`
  text-align: center;
  margin-top: ${gutter(2)};
`;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 ${gutter(4)};
  min-height: 100vh;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  width: 100%;
`;

const Col = styled.div`
  width: calc(25% - ${gutter(2)});
  min-width: 260px;
  padding: 0 ${gutter(2)};
  border-left: 2px solid ${color('gray20')};
  border-right: 2px solid ${color('gray20')};

  &:first-child {
    border-left: 2px solid transparent;
  }
  &:last-child {
    border-right: 2px solid transparent;
  }
`;

const LotNumber = styled.div`
  ${caps14};
  color: ${color('gray80')};
  text-align: center;
  margin-bottom: ${gutter(1)};
`;

const Table = styled(LeaderboardTable)`
  margin-top: ${gutter(4)};
`;

const LotHeading = styled.div`
  ${m24};
  text-align: center;
  margin-bottom: ${gutter(0.5)};
  margin-top: ${gutter(0.5)};
`;

const LotSubheading = styled.div`
  ${m18};
  text-align: center;
  margin-bottom: ${gutter(0.5)};
  margin-top: ${gutter(0.5)};
  font-style: italic;
  min-height: 2.5em;
`;

const Subline = styled.div`
  margin: ${gutter(4)} 0;
  ${p14};
  font-weight: 500;
  text-transform: uppercase;
  text-align: center;
`;

const MAX_LEADERBOARDS = 8;

export function Leaderboards() {
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
  });
  const { year, lotNumbers } = useParams();
  const leaderboards = useLeaderboardsQuery({
    variables: {
      filter: {
        year,
        lotNumbers: lotNumbers
          ?.split(',')
          .slice(0, MAX_LEADERBOARDS)
          .map((l) => Number(l)),
      },
    },
    pollInterval: 15_000,
    skip: !lotNumbers,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    let sub: any;
    if (me.data?.activeAuction?.id && leaderboards) {
      sub = leaderboards.subscribeToMore({
        document: LiveAuctionFeedDocument,
        variables: { auctionId: me.data?.activeAuction?.id },
        updateQuery(prev, { subscriptionData }) {
          // @todo add bid into leaderboard and sort by amount
          if (!subscriptionData.data) {
            return prev;
          }
          const {
            liveAuctionFeed: { lot, bid },
          } = subscriptionData.data as unknown as LiveAuctionFeedSubscription;

          return Object.assign({}, prev, {
            allLots: {
              ...prev.allLots,
              edges: prev.allLots.edges.map((edge) => {
                if (lot && bid && edge.node.id === lot.id) {
                  const leaderboard = [
                    {
                      id: bid.id,
                      amount: bid.amount,
                      createdAt: bid.createdAt,
                      paddleId: bid.paddle.id,
                      paddleNumber: bid.paddle.number,
                      totalBids: 0,
                      rank: 1,
                    },
                  ]
                    .filter(
                      (value, index, self) =>
                        index === self.findIndex((t) => t.id === value.id)
                    )
                    .sort((a, b) => b.amount - a.amount)
                    .map((n, i) => ({
                      ...n,
                      rank: i + 1,
                      __typename: 'LeaderboardRank',
                    }));
                  return {
                    ...edge,
                    node: {
                      ...edge.node,
                      leaderboard,
                    },
                  };
                }
                return edge;
              }),
            },
          });
        },
      });
      return () => sub?.unsubscribe?.();
    }
  }, [leaderboards, me.data]);

  return (
    <Container>
      <div>
        <Row>
          {(leaderboards.data?.allLots?.edges || []).map(({ node: lot }) => {
            const now = Date.now();
            const isLotEnded = now > (Date.parse(lot.endsAt || '') || now);

            return (
              <Col key={lot.id}>
                <LotNumber>Lot {lot.number}</LotNumber>
                <LotHeading>{lot.vintner}</LotHeading>
                <LotSubheading>{lot.summary || lot.title}</LotSubheading>
                <StyleLotTimer startsAt={lot.startsAt} endsAt={lot.endsAt} />
                <Table>
                  <thead>
                    <tr>
                      <th>Place</th>
                      <th>Paddle</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TransitionGroup component={null}>
                      {(lot.leaderboard || []).map((rank) => (
                        <Transition key={rank.id} timeout={1000}>
                          {(state) =>
                            state === 'exiting' ? null : (
                              <tr
                                data-highlight={
                                  state === 'entering' ? 'true' : 'false'
                                }
                              >
                                <td>{printRank(rank.rank)}</td>
                                <td>#{rank.paddleNumber}</td>
                                <td>
                                  <FormatAmount
                                    value={rank.amount}
                                    buyersPremiumPercentage={
                                      isLotEnded
                                        ? me?.data?.activeAuction
                                            ?.buyersPremiumPercentage
                                        : undefined
                                    }
                                    overheadPremiumPercentage={
                                      isLotEnded
                                        ? me?.data?.activeAuction
                                            ?.overheadPremiumPercentage
                                        : undefined
                                    }
                                  />
                                </td>
                              </tr>
                            )
                          }
                        </Transition>
                      ))}
                    </TransitionGroup>
                    {Array.from({
                      length: 10 - (lot.leaderboard || []).length,
                    }).map((n, i) => (
                      <tr key={i}>
                        <td colSpan={6} placeholder="true">
                          {printRank((lot.leaderboard || []).length + i + 1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            );
          })}
        </Row>
        <Subline>The top 10 bids on each lot win a 12-bottle case.</Subline>
      </div>
    </Container>
  );
}
